import { useMemo, useRef, useState } from "react";
import {
  ColDef,
  ColumnState,
  RowClassParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import {
  aggridHeaderClass,
  pdaCellStyle,
} from "src/components/common/common";
import { PDA1050_R01DetailDto } from "./PDA1050_R01Dto";
import usePDA1050_R01Store from "./Store_PDA1050_R01";

type PDA1050_R01DetailDtoWithInserted = PDA1050_R01DetailDto & {
  inserted?: boolean;
};

const ACTIVE_ROW_BG = "#fef9c3";

/**
 * 선택 발주의 품목별 발주량, 누적 입고량 및 현재 검수량을 표시하는 상세 그리드다.
 * 현재 스캔 품목과 최근 검수 품목을 상단에 배치하고 검수된 행을 강조한다.
 */
const PDA1050_R01Table = () => {
  const {
    PDA1050_R01DetailList,
    checkedPDA1050_R01List,
    scannedItem,
  } = usePDA1050_R01Store();
  const gridRef = useRef<AgGridReact<PDA1050_R01DetailDtoWithInserted>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  /** 품목코드별 현재 검수 수량을 합산하여 검수량 컬럼에서 즉시 조회할 수 있는 Map을 만든다. */
  const checkedQtyMap = useMemo(() => {
    const quantityMap = new Map<string, number>();
    checkedPDA1050_R01List.forEach((item) => {
      const currentQty = quantityMap.get(item.itemCd) || 0;
      quantityMap.set(item.itemCd, currentQty + item.qty);
    });
    return quantityMap;
  }, [checkedPDA1050_R01List]);

  /** 검수된 행의 강조 여부를 빠르게 판단할 수 있도록 품목코드 Set을 만든다. */
  const checkedItemCdSet = useMemo(
    () => new Set(checkedPDA1050_R01List.map((item) => item.itemCd)),
    [checkedPDA1050_R01List]
  );

  /** 검수 목록에 포함된 품목 행에 화면 전용 활성 행 클래스를 적용한다. */
  const rowClassRules = useMemo(
    () => ({
      "pda1050-active-row": (
        params: RowClassParams<PDA1050_R01DetailDtoWithInserted>
      ) => {
        const itemCd = params.data?.itemCd;
        return !!itemCd && checkedItemCdSet.has(itemCd);
      },
    }),
    [checkedItemCdSet]
  );

  /**
   * 현재 스캔 품목을 최우선, 최근 검수 품목을 그다음 순서로 배치한다.
   * 우선순위 대상이 없으면 서버에서 받은 원래 발주 품목 순서를 그대로 유지한다.
   */
  const paddedGridData = useMemo(() => {
    const priorityOrder: string[] = [];

    if (scannedItem?.itemCd) {
      priorityOrder.push(scannedItem.itemCd);
    }

    [...checkedPDA1050_R01List].reverse().forEach((item) => {
      if (!priorityOrder.includes(item.itemCd)) {
        priorityOrder.push(item.itemCd);
      }
    });

    if (priorityOrder.length === 0) {
      return [...PDA1050_R01DetailList];
    }

    const priorityItemCdSet = new Set(priorityOrder);
    const priorityRows: PDA1050_R01DetailDtoWithInserted[] = [];
    const remainingRows: PDA1050_R01DetailDtoWithInserted[] = [];

    PDA1050_R01DetailList.forEach((detail) => {
      if (priorityItemCdSet.has(detail.itemCd)) {
        priorityRows.push(detail);
      } else {
        remainingRows.push(detail);
      }
    });

    priorityRows.sort(
      (first, second) =>
        priorityOrder.indexOf(first.itemCd) -
        priorityOrder.indexOf(second.itemCd)
    );

    return [...priorityRows, ...remainingRows];
  }, [PDA1050_R01DetailList, checkedPDA1050_R01List, scannedItem]);

  /**
   * 상세 그리드의 품명, 발주량, 누적 입고량 및 현재 검수량 컬럼을 정의한다.
   * 완료 품목, 기존 입고 품목, 현재 검수 품목을 배경색과 글자색으로 구분한다.
   */
  const columnDefs = useMemo(
    () =>
      [
        {
          headerName: "품명",
          field: "itemNm",
          valueGetter: (params) => params.data?.itemNm || "-",
          cellStyle: (params) => {
            const itemCd = params.data?.itemCd;
            const isActive = !!itemCd && checkedItemCdSet.has(itemCd);
            return {
              textAlign: "left",
              ...pdaCellStyle,
              whiteSpace: "pre-line",
              ...(isActive ? { backgroundColor: ACTIVE_ROW_BG } : {}),
            };
          },
          headerClass: aggridHeaderClass,
          flex: 2,
          minWidth: 90,
        },
        {
          headerName: "발주량",
          field: "orderQty",
          valueGetter: (params) => params.data?.orderQty ?? null,
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.value === null || params.value === undefined) return "-";
            return params.value.toLocaleString();
          },
          cellStyle: (params) => {
            const itemCd = params.data?.itemCd;
            const isActive = !!itemCd && checkedItemCdSet.has(itemCd);
            return {
              textAlign: "right",
              ...pdaCellStyle,
              justifyContent: "flex-end",
              ...(isActive ? { backgroundColor: ACTIVE_ROW_BG } : {}),
            };
          },
          headerClass: aggridHeaderClass,
          flex: 1,
          minWidth: 80,
        },
        {
          headerName: "입고량",
          field: "transportQty",
          valueGetter: (params) => params.data?.transportQty ?? 0,
          valueFormatter: (params: ValueFormatterParams) =>
            (params.value ?? 0).toLocaleString(),
          cellStyle: (params) => {
            const detail = params.data as PDA1050_R01DetailDto;
            if (!detail) {
              return {
                textAlign: "right",
                ...pdaCellStyle,
                justifyContent: "flex-end",
              };
            }

            const isActive = checkedItemCdSet.has(detail.itemCd);
            let backgroundColor = "white";
            if (isActive) {
              backgroundColor = ACTIVE_ROW_BG;
            } else if (detail.remainQty === 0) {
              backgroundColor = "#dcfce7";
            } else if (detail.transportQty > 0) {
              backgroundColor = ACTIVE_ROW_BG;
            }

            return {
              textAlign: "right",
              ...pdaCellStyle,
              backgroundColor,
              fontWeight: "bold",
              color: "#2563eb",
              justifyContent: "flex-end",
            };
          },
          headerClass: aggridHeaderClass,
          flex: 1,
          minWidth: 80,
        },
        {
          headerName: "검수량",
          valueGetter: (params) => {
            const detail = params.data as PDA1050_R01DetailDto;
            if (!detail) return 0;
            return checkedQtyMap.get(detail.itemCd) || 0;
          },
          valueFormatter: (params: ValueFormatterParams) =>
            (params.value ?? 0).toLocaleString(),
          cellStyle: (params) => {
            const detail = params.data as PDA1050_R01DetailDto;
            const value = params.value ?? 0;
            const isActive =
              !!detail && checkedItemCdSet.has(detail.itemCd);
            return {
              textAlign: "right",
              ...pdaCellStyle,
              color: value > 0 ? "#7c3aed" : "#9ca3af",
              fontWeight: value > 0 ? "bold" : "normal",
              justifyContent: "flex-end",
              ...(isActive ? { backgroundColor: ACTIVE_ROW_BG } : {}),
            };
          },
          headerClass: aggridHeaderClass,
          flex: 1,
          minWidth: 80,
        },
      ] as ColDef<PDA1050_R01DetailDtoWithInserted>[],
    [checkedItemCdSet, checkedQtyMap]
  );

  /** GridContextWrapper가 요구하는 선택 초기화 콜백이며, 상세 표는 행 선택 상태를 사용하지 않는다. */
  const initSelected = () => {};

  return (
    <div className="pda1050-r01-table flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden">
      <style>{`
        .pda1050-r01-table .ag-row.pda1050-active-row .ag-cell {
          background-color: ${ACTIVE_ROW_BG};
        }
      `}</style>
      <GridContextWrapper<PDA1050_R01DetailDtoWithInserted>
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={paddedGridData}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1050_R01_Table_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<PDA1050_R01DetailDtoWithInserted>
            ref={gridRef}
            rowData={paddedGridData}
            columnDefs={columnDefs}
            rowClassRules={rowClassRules}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1050_R01_Table_ColumnState"
            setColumnState={setColumnState}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1050_R01Table;
